import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { LeanAuthHttpAdapter } from '../../server/auth/contracts.js';
import { MAX_NICKNAME_LENGTH } from '../../server/presence.js';
import {
  activeDisplayName,
  createLeanAuthController,
  type AuthBootstrapState,
  type LeanAuthController,
} from './controller.js';
import type { GisButtonCleanup, GisButtonRenderer } from './gis.js';
import { DelayedLoader } from '../ui/kit/index.js';
import { Button, TextField } from '../ui/kit/index.js';
import { LogoMark } from '../ui/Logo.js';
import { PixelIcon } from '../ui/PixelIcon.js';
import { FeedbackButton } from '../feedback/index.js';
import { ChangelogButton } from '../changelog/index.js';
import { ConfirmDialog } from '../decks/ConfirmDialog.js';
import { handleInAppLinkClick, useCurrentPath } from '../client/navigation.js';

export interface AuthRenderContext {
  state: AuthBootstrapState;
  /** Server profile is authoritative only for an active account session. */
  activeDisplayName?: string;
}

export interface AuthBootstrapProps {
  controller?: LeanAuthController;
  adapter?: LeanAuthHttpAdapter;
  gisRenderer: GisButtonRenderer;
  returnTo: string;
  nicknamePrefill?: string;
  children?: ReactNode | ((context: AuthRenderContext) => ReactNode);
}

function ApplicationShell({
  children,
  account,
  logout,
  hideMenu = false,
}: {
  children: ReactNode;
  /** Nickname block; the feedback/changelog glyphs sit between it and logout. */
  account?: ReactNode;
  logout?: ReactNode;
  hideMenu?: boolean;
}): ReactElement {
  const path = useCurrentPath();
  // The menu marks the section the reader is in (design system 02 §9).
  const current = (section: string): 'page' | undefined => (
    section === '/' ? path === '/' : path === section || path.startsWith(`${section}/`)
  ) ? 'page' : undefined;
  return (
    <div className={hideMenu ? 'auth-app-shell auth-app-shell-menu-hidden' : 'auth-app-shell'}>
      {hideMenu ? null : (
        <nav className="auth-top-menu" aria-label="Меню проекта">
          <div className="auth-menu-links">
            <LogoMark size={24} className="auth-menu-logo" />
            <a className="auth-home-link" data-onboarding="header-home" href="/" aria-current={current('/')} onClick={handleInAppLinkClick}>
              <PixelIcon name="home" className="auth-menu-icon" />
              <span className="auth-menu-label">Главная</span>
            </a>
            <a className="auth-home-link" data-onboarding="header-decks" href="/decks" aria-current={current('/decks')} onClick={handleInAppLinkClick}>
              <PixelIcon name="decks" className="auth-menu-icon" />
              <span className="auth-menu-label">Колоды</span>
            </a>
            <a className="auth-home-link" href="/challenges" aria-current={current('/challenges')} onClick={handleInAppLinkClick}>
              <PixelIcon name="challenges" className="auth-menu-icon" />
              <span className="auth-menu-label">Челленджи</span>
            </a>
            <a className="auth-home-link" data-onboarding="header-sandbox" href="/lab/sandbox" aria-current={current('/lab/sandbox')} onClick={handleInAppLinkClick}>
              <PixelIcon name="sandbox" className="auth-menu-icon" />
              <span className="auth-menu-label">Песочница</span>
            </a>
          </div>
          <div className="auth-menu-end">
            {account}
            <FeedbackButton />
            <ChangelogButton />
            {logout}
          </div>
        </nav>
      )}
      {/* data-app-scroll-root: this row owns the app's scrollbar (the shell
          itself is overflow:hidden) and outlives route swaps, so navigation
          resets its offset. See client/navigation.ts. */}
      <div className="auth-app-content" data-app-scroll-root="">{children}</div>
    </div>
  );
}

function childrenFor(
  children: AuthBootstrapProps['children'],
  state: AuthBootstrapState,
): ReactNode {
  if (typeof children === 'function') {
    const displayName = activeDisplayName(state);
    return children({ state, ...(displayName !== undefined ? { activeDisplayName: displayName } : {}) });
  }
  return children;
}

function BlockingStatus({ children }: { children: ReactNode }): ReactElement {
  return (
    <ApplicationShell hideMenu>
      <main className="auth-blocking-shell">
        <section className="auth-blocking-panel">
          {children}
        </section>
      </main>
    </ApplicationShell>
  );
}

function GoogleSignInButton({
  adapter,
  renderer,
  returnTo,
}: {
  adapter: LeanAuthHttpAdapter;
  renderer: GisButtonRenderer;
  returnTo: string;
}): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const intentRequestRef = useRef<{
    attempt: number;
    promise: ReturnType<LeanAuthHttpAdapter['createGoogleLoginIntent']>;
  } | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    let cleanup: GisButtonCleanup | undefined;
    setStatus('loading');
    setError(undefined);
    const container = containerRef.current;
    if (container === null) return undefined;
    container.replaceChildren();
    if (intentRequestRef.current?.attempt !== attempt) {
      intentRequestRef.current = {
        attempt,
        promise: adapter.createGoogleLoginIntent({ returnTo }),
      };
    }
    void intentRequestRef.current.promise
      .then((intent) => active ? renderer.render(container, intent) : undefined)
      .then((dispose) => {
        if (typeof dispose === 'function') cleanup = dispose;
        if (active) setStatus('ready');
        else cleanup?.();
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setStatus('error');
        setError(reason instanceof Error ? reason.message.slice(0, 200) : 'Вход через Google недоступен.');
      });
    return () => {
      active = false;
      cleanup?.();
    };
  }, [adapter, attempt, renderer, returnTo]);

  return (
    <div aria-label="Вход через Google" role="group">
      <div ref={containerRef} data-testid="google-sign-in-button" />
      {status === 'loading' ? <span role="status">Готовим безопасный вход…</span> : null}
      {status === 'error' ? (
        <div role="alert">
          <span>{error ?? 'Вход через Google недоступен.'}</span>{' '}
          <Button variant="secondary" size="sm" onClick={() => setAttempt((value) => value + 1)}>
            Повторить
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ProfileForm({
  controller,
  initialValue,
  submitLabel,
}: {
  controller: LeanAuthController;
  initialValue: string;
  submitLabel: string;
}): ReactElement {
  const [displayName, setDisplayName] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(): Promise<void> {
    if (saving) return;
    setSaving(true);
    setError(undefined);
    try {
      await controller.saveProfile(displayName);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить профиль.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="auth-profile-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <label className="auth-profile-label" htmlFor="account-display-name">Публичный ник</label>
      <div className="auth-profile-row">
        <TextField
          className="auth-profile-input"
          id="account-display-name"
          name="displayName"
          placeholder="Ваш ник"
          autoFocus
          autoComplete="nickname"
          maxLength={MAX_NICKNAME_LENGTH * 2}
          value={displayName}
          disabled={saving}
          onChange={(event) => setDisplayName(event.currentTarget.value)}
        />
        <Button className="auth-primary-button" type="submit" disabled={saving} aria-busy={saving}>
          {saving ? 'Сохраняем…' : submitLabel}
        </Button>
      </div>
      {error !== undefined ? <div className="auth-form-error" role="alert">{error}</div> : null}
    </form>
  );
}

function CurrentLogoutButton({
  controller,
  inMenu = false,
}: {
  controller: LeanAuthController;
  inMenu?: boolean;
}): ReactElement {
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string>();
  // The menu trigger is a bare glyph, so it asks before dropping the session;
  // the account panel keeps its worded one-click button.
  const [confirming, setConfirming] = useState(false);

  async function logout(): Promise<void> {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(undefined);
    try {
      await controller.logoutCurrent();
    } catch (reason) {
      setLogoutError(reason instanceof Error ? reason.message : 'Не удалось выйти.');
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <div className={inMenu ? 'auth-menu-logout-wrap' : undefined}>
      <Button
        variant="secondary"
        size="sm"
        className={inMenu ? 'auth-menu-logout' : 'auth-secondary-button'}
        disabled={loggingOut}
        aria-busy={loggingOut}
        {...(inMenu ? { title: loggingOut ? 'Выходим…' : 'Выйти' } : {})}
        onClick={() => {
          if (inMenu) {
            setConfirming(true);
            return;
          }
          void logout();
        }}
      >
        {inMenu ? (
          <>
            <PixelIcon name="logout" className="auth-menu-icon" />
            <span className="auth-menu-label">{loggingOut ? 'Выходим…' : 'Выйти'}</span>
          </>
        ) : (loggingOut ? 'Выходим…' : 'Выйти')}
      </Button>
      {logoutError !== undefined ? (
        <div className={inMenu ? 'auth-menu-error' : 'auth-form-error'} role="alert">
          {logoutError}
        </div>
      ) : null}
      {confirming ? (
        <ConfirmDialog
          title="Выйти из аккаунта?"
          description="Сессия на этом устройстве закроется. Колоды и прогресс останутся на аккаунте."
          confirmLabel="Выйти"
          busyLabel="Выходим…"
          busy={loggingOut}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { void logout().finally(() => setConfirming(false)); }}
        />
      ) : null}
    </div>
  );
}

function Onboarding({
  controller,
  nicknamePrefill,
}: {
  controller: LeanAuthController;
  nicknamePrefill: string;
}): ReactElement {
  return (
    <BlockingStatus>
      <h1>Как вас показывать в игре?</h1>
      <p>Выберите ник</p>
      <ProfileForm controller={controller} initialValue={nicknamePrefill} submitLabel="Продолжить" />
      <CurrentLogoutButton controller={controller} />
    </BlockingStatus>
  );
}

function AccountPanel({
  state,
  controller,
  adapter,
  gisRenderer,
  returnTo,
}: {
  state: Extract<AuthBootstrapState, { kind: 'signed_out' | 'provider_degraded' }>;
  controller: LeanAuthController;
  adapter: LeanAuthHttpAdapter;
  gisRenderer: GisButtonRenderer;
  returnTo: string;
}): ReactElement {
  return (
    <aside
      className="auth-account-panel"
      aria-label="Аккаунт"
    >
      {state.kind === 'provider_degraded' ? (
        <div role="status">
          <span>Google-вход временно недоступен.</span>{' '}
          <Button variant="secondary" size="sm" onClick={() => { void controller.retry(); }}>Проверить снова</Button>
        </div>
      ) : state.config.loginIssuance === 'available' ? (
        <GoogleSignInButton adapter={adapter} renderer={gisRenderer} returnTo={returnTo} />
      ) : (
        <span role="status">Вход временно отключён.</span>
      )}
    </aside>
  );
}

function RequiredLogin({
  state,
  controller,
  adapter,
  gisRenderer,
  returnTo,
}: {
  state: Extract<AuthBootstrapState, { kind: 'signed_out' | 'provider_degraded' }>;
  controller: LeanAuthController;
  adapter: LeanAuthHttpAdapter;
  gisRenderer: GisButtonRenderer;
  returnTo: string;
}): ReactElement {
  return (
    <BlockingStatus>
      <h1>Вход</h1>
      <p>Чтобы открыть игру, войдите через Google.</p>
      {state.kind === 'provider_degraded' ? (
        <div role="status">
          <span>Google-вход временно недоступен.</span>{' '}
          <Button variant="secondary" size="sm" onClick={() => { void controller.retry(); }}>Проверить снова</Button>
        </div>
      ) : state.config.loginIssuance === 'available' ? (
        <GoogleSignInButton adapter={adapter} renderer={gisRenderer} returnTo={returnTo} />
      ) : (
        <span role="status">Вход временно отключён.</span>
      )}
    </BlockingStatus>
  );
}

export function AuthBootstrap({
  controller: suppliedController,
  adapter: suppliedAdapter,
  gisRenderer,
  returnTo,
  nicknamePrefill = '',
  children,
}: AuthBootstrapProps): ReactElement {
  const controllerRef = useRef<LeanAuthController | undefined>(suppliedController);
  if (controllerRef.current === undefined) {
    if (suppliedAdapter === undefined) throw new Error('AuthBootstrap требует controller или adapter');
    controllerRef.current = createLeanAuthController(suppliedAdapter);
  }
  const controller = controllerRef.current;
  const adapter = suppliedAdapter;
  if (adapter === undefined) {
    throw new Error('AuthBootstrap требует adapter для Google login UI');
  }
  const state = useSyncExternalStore(controller.subscribe, controller.getState, controller.getState);

  useEffect(() => { void controller.bootstrap(); }, [controller]);

  if (state.kind === 'loading') {
    return <DelayedLoader />;
  }
  if (state.kind === 'error') {
    return (
      <BlockingStatus>
        <p role="alert">{state.message}</p>
        <Button variant="secondary" size="sm" onClick={() => { void controller.retry(); }}>Повторить</Button>
      </BlockingStatus>
    );
  }
  if (state.kind === 'pending_profile') {
    return <Onboarding controller={controller} nicknamePrefill={nicknamePrefill} />;
  }
  if (state.kind === 'disabled') {
    return <ApplicationShell>{childrenFor(children, state)}</ApplicationShell>;
  }
  if ((state.kind === 'signed_out' || state.kind === 'provider_degraded')
    && state.config.authPolicy === 'required') {
    return (
      <RequiredLogin
        state={state}
        controller={controller}
        adapter={adapter}
        gisRenderer={gisRenderer}
        returnTo={returnTo}
      />
    );
  }
  const account = state.kind === 'active' ? (
    <div className="auth-menu-account" aria-label="Аккаунт">
      <span className="auth-menu-nickname" title={state.session.profile.displayName}>
        {state.session.profile.displayName}
      </span>
    </div>
  ) : null;
  return (
    <ApplicationShell
      account={account}
      logout={state.kind === 'active' ? <CurrentLogoutButton controller={controller} inMenu /> : null}
    >
      {childrenFor(children, state)}
      {state.kind === 'active' ? null : (
        <AccountPanel
          state={state}
          controller={controller}
          adapter={adapter}
          gisRenderer={gisRenderer}
          returnTo={returnTo}
        />
      )}
    </ApplicationShell>
  );
}
